import { CANONICAL_COMMANDS, normalizeCommandName } from '../core/commands/canonicalCommands';
import { useCommandPresetStore } from '../state/useCommandPresetStore';
import { useAcadPgpStore } from '../state/useAcadPgpStore';

/**
 * acad.pgp import (2D AutoCAD compatibility)
 *
 * Parses an AutoCAD Program Parameters file (`acad.pgp`) — the stock file
 * that ships with AutoCAD defines one alias per line (`L, *LINE`) — and
 * registers each alias as a callable command-line name on the canonical
 * command system, exactly like the Rhino Options ini import does for the
 * 3D workbench.
 *
 * Mapping policy:
 * - Alias → AutoCAD command name → canonical id via the 2D name pool first
 *   (it is spelled AutoCAD-style), then the 3D pool as fallback. The alias
 *   becomes the PRIMARY command-line name of the resolved command in the
 *   "AutoCAD Import" preset (fixed id `import.acad`); built-in names stay
 *   callable and re-import replaces the preset in place.
 * - Commands that live only on the legacy registry (SAVE, OPEN, ...) can't
 *   ride the preset system; their aliases are stored as "extras" that the
 *   dispatcher resolves against the legacy registry after the CAD parser.
 * - Unmapped commands (no app equivalent) are reported as skipped, never
 *   silently dropped.
 * - acad.pgp is a COMMAND-LINE alias file, not a keyboard file: no keyboard
 *   shortcuts are assigned (unlike the Rhino ini import).
 */

/** One parsed alias entry from a pgp file. */
export interface AcadPgpEntry {
    /** Typed alias, uppercased (`L`, `PL`, `DT`). */
    alias: string;
    /** Target AutoCAD command name, `*` prefix stripped (`LINE`). */
    command: string;
    /** 1-based source line, for reports. */
    line: number;
}

export class AcadPgpParseError extends Error {}

/**
 * Read the picked pgp file honoring its encoding. AutoCAD writes ANSI/UTF-8
 * on Windows, but some editors save UTF-16 — the BOM is sniffed from the raw
 * bytes and the matching decoder is used (same rule as the Rhino ini reader).
 */
export async function readAcadPgpFile(file: File): Promise<string> {
    const bytes = new Uint8Array(await file.arrayBuffer());
    if (bytes[0] === 0xff && bytes[1] === 0xfe) {
        return new TextDecoder('utf-16le').decode(bytes);
    }
    if (bytes[0] === 0xfe && bytes[1] === 0xff) {
        return new TextDecoder('utf-16be').decode(bytes);
    }
    return new TextDecoder('utf-8').decode(bytes);
}

/**
 * Parse a pgp file into alias entries. Line grammar: `alias,*command`;
 * `;`/`;;` full-line comments and trailing `; comment` text after the comma
 * are dropped; blank lines are skipped; invalid lines are reported as
 * entries with empty fields rather than silently dropped.
 */
export function parseAcadPgp(text: string): AcadPgpEntry[] {
    const entries: AcadPgpEntry[] = [];
    const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/);
    for (let i = 0; i < lines.length; i++) {
        const raw = lines[i].trim();
        if (!raw || raw.startsWith(';')) continue;
        // Trailing comment: only cut when the ';' sits after the comma.
        const comma = raw.indexOf(',');
        const semi = raw.indexOf(';');
        const line = semi > comma ? raw.slice(0, semi).trim() : raw;
        const split = line.indexOf(',');
        if (split <= 0) {
            entries.push({ alias: '', command: '', line: i + 1 });
            continue;
        }
        const alias = line.slice(0, split).trim().toUpperCase();
        const command = line
            .slice(split + 1)
            .trim()
            .replace(/^\*+/, '')
            .trim();
        entries.push({ alias, command, line: i + 1 });
    }
    return entries;
}

// ---------------------------------------------------------------------------
// Command-name resolution
// ---------------------------------------------------------------------------

/**
 * Canonical name index over both family pools: normalized AutoCAD command
 * name → `cmd.<canonicalId>`. The 2D pool is spelled AutoCAD-style so it is
 * consulted first; the 3D pool is a fallback. First writer wins per name.
 */
const ACAD_NAME_INDEX = new Map<string, string>();
for (const cmd of CANONICAL_COMMANDS) {
    const storeId = `cmd.${cmd.id}`;
    for (const pool of [cmd.names['2d'], cmd.names['3d']]) {
        for (const name of pool) {
            const key = normalizeCommandName(name);
            if (!key || ACAD_NAME_INDEX.has(key)) continue;
            ACAD_NAME_INDEX.set(key, storeId);
        }
    }
}

/**
 * AutoCAD command names whose app equivalent lives on the legacy registry
 * (plain ids, resolved by the dispatcher after the CAD parser). These can't
 * ride the preset system, so their aliases are stored as pgp extras.
 */
const ACAD_COMMAND_EXTRA: Record<string, string> = {
    UNDO: 'undo',
    U: 'undo',
    REDO: 'redo',
    SAVE: 'saveProject',
    QSAVE: 'saveProject',
    SAVEAS: 'saveProjectAs',
    NEW: 'newProject',
    OPEN: 'openProject',
    PASTECLIP: 'paste',
    CUTCLIP: 'cut',
};

export type AcadPgpSkipReason =
    | 'unmapped-command'
    | 'duplicate-alias'
    | 'invalid-line';

export interface AcadPgpApplied {
    alias: string;
    canonicalId: string;
    commandLabel: string;
}

/** Alias bound to a legacy-registry command (dispatcher extras, not presets). */
export interface AcadPgpLegacyApplied {
    alias: string;
    legacyId: string;
}

export interface AcadPgpSkipped {
    alias: string;
    command: string;
    line: number;
    reason: AcadPgpSkipReason;
}

/** A built-in name of ANOTHER command shadowed by the imported alias. */
export interface AcadPgpCollision {
    alias: string;
    shadowedId: string;
    shadowedLabel: string;
}

export interface AcadPgpImportSummary {
    /** Valid `alias,*command` rows in the file (comments/blanks excluded). */
    totalEntries: number;
    applied: AcadPgpApplied[];
    legacyApplied: AcadPgpLegacyApplied[];
    skipped: AcadPgpSkipped[];
    collisions: AcadPgpCollision[];
    presetAliases: number;
    presetCommands: number;
    presetCreated: boolean;
}

/** Fixed id of the preset carrying imported acad.pgp command-line names. */
export const ACAD_IMPORT_PRESET_ID = 'import.acad';
export const ACAD_IMPORT_PRESET_NAME = 'AutoCAD Import';

interface ResolvedMapping {
    alias: string;
    canonicalId: string;
}

/** Canonical command the pgp command name resolves to, if any. */
function resolveCanonical(command: string): string | null {
    return ACAD_NAME_INDEX.get(normalizeCommandName(command)) ?? null;
}

/**
 * Apply already-resolved alias → canonicalId mappings to the import preset
 * (used by both importAcadPgp and REINIT). Compute collisions against the
 * built-in name pools and return the summary. Extras (legacy targets) are
 * consulted live by the dispatcher from the pgp store.
 */
function applyResolved(
    mappings: ResolvedMapping[],
    legacy: AcadPgpLegacyApplied[]
): AcadPgpImportSummary {
    const overrides: Record<string, string[]> = {};
    const applied: AcadPgpApplied[] = [];
    const labelById = new Map(CANONICAL_COMMANDS.map((c) => [c.id, c.label]));
    const known = new Set(CANONICAL_COMMANDS.map((c) => c.id));
    for (const { alias, canonicalId } of mappings) {
        if (!known.has(canonicalId)) continue;
        (overrides[canonicalId] ??= []).push(alias);
        applied.push({ alias, canonicalId, commandLabel: labelById.get(canonicalId) ?? canonicalId });
    }

    const presetResult =
        Object.keys(overrides).length > 0 || legacy.length > 0
            ? useCommandPresetStore.getState().upsertImportedPreset({
                  id: ACAD_IMPORT_PRESET_ID,
                  name: ACAD_IMPORT_PRESET_NAME,
                  overrides,
                  suggestionPriority: ['2d', '3d'],
                  baseFamily: '2d',
                  notes: 'Imported from acad.pgp: aliases are the primary command-line names; built-in names stay callable. Re-import replaces these overrides.',
              })
            : null;

    // An imported alias that is also a built-in name of a DIFFERENT command
    // shadows that spelling (preset overrides pre-claim in the name index);
    // report the shadowed command so nothing disappears silently.
    const collisions: AcadPgpCollision[] = [];
    const aliasKeys = new Set(mappings.map((m) => normalizeCommandName(m.alias)));
    for (const key of aliasKeys) {
        for (const cmd of CANONICAL_COMMANDS) {
            const owns = [...cmd.names['2d'], ...cmd.names['3d']].some(
                (n) => normalizeCommandName(n) === key
            );
            if (owns && !applied.some((a) => a.canonicalId === cmd.id && normalizeCommandName(a.alias) === key)) {
                collisions.push({ alias: key, shadowedId: cmd.id, shadowedLabel: cmd.label });
            }
        }
    }

    return {
        totalEntries: mappings.length + legacy.length,
        applied,
        legacyApplied: legacy,
        skipped: [],
        collisions,
        presetAliases: applied.length,
        presetCommands: Object.keys(overrides).length,
        presetCreated: presetResult?.created ?? false,
    };
}

/**
 * Import a pgp file: parse → resolve → apply. The first entry for an alias
 * wins (later duplicates are reported as skipped). Returns the summary; the
 * caller persists the resolved mappings via useAcadPgpStore.
 */
export function importAcadPgp(text: string): AcadPgpImportSummary {
    const parsed = parseAcadPgp(text);
    const valid = parsed.filter((e) => e.alias && e.command);
    const invalid = parsed.filter((e) => !e.alias || !e.command);
    if (valid.length === 0) {
        throw new AcadPgpParseError('The file contains no acad.pgp alias entries.');
    }

    const mappings: ResolvedMapping[] = [];
    const legacy: AcadPgpLegacyApplied[] = [];
    const skipped: AcadPgpSkipped[] = [];
    const claimed = new Set<string>(); // lowercase alias, first file entry wins

    for (const entry of valid) {
        if (claimed.has(entry.alias.toLowerCase())) {
            skipped.push({ ...entry, reason: 'duplicate-alias' });
            continue;
        }
        const canonicalId = resolveCanonical(entry.command);
        const legacyId = ACAD_COMMAND_EXTRA[normalizeCommandName(entry.command)];
        if (canonicalId) {
            claimed.add(entry.alias.toLowerCase());
            mappings.push({ alias: entry.alias, canonicalId });
        } else if (legacyId) {
            claimed.add(entry.alias.toLowerCase());
            legacy.push({ alias: entry.alias, legacyId });
        } else {
            skipped.push({ ...entry, reason: 'unmapped-command' });
        }
    }
    for (const entry of invalid) {
        skipped.push({ ...entry, reason: 'invalid-line' });
    }

    const summary = applyResolved(mappings, legacy);
    summary.totalEntries = valid.length;
    summary.skipped = skipped;
    return summary;
}

/**
 * REINIT: re-apply the persisted pgp import (resolved mappings from the pgp
 * store) to the import preset. Extras stay registered (the dispatcher reads
 * them live); canonical mappings are rebuilt through the preset system.
 */
export function reapplyAcadPgp(): AcadPgpImportSummary {
    const { entries, extras } = useAcadPgpStore.getState();
    return applyResolved(
        entries.map((e) => ({ alias: e.alias, canonicalId: e.canonicalId })),
        extras.map((e) => ({ alias: e.alias, legacyId: e.legacyId }))
    );
}
